import React from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import theme from "./src/theme";
import ScrollToTopButton from "./src/components/ScrollToTopButton";

// Global CSS für responsive Tabellen
import "./src/styles/global.css";

// Cookiebot nach User-Interaktion laden (LCP/INP Optimierung)
export const onInitialClientRender = () => {
  if (typeof window === "undefined") return;

  // --- GA4 (Consent-gesteuert) ---
  const GA4_ID = "G-NCH442JG2W";

  const hasAnalyticsConsent = () => {
    try {
      // Cookiebot Consent API (typisch): Cookiebot.consent.statistics === true
      if (window.Cookiebot?.consent && typeof window.Cookiebot.consent.statistics === "boolean") {
        return window.Cookiebot.consent.statistics === true;
      }

      // Fallback: Cookiebot kann alternative Objekte exposen; im Zweifel: kein Consent
      return false;
    } catch (e) {
      return false;
    }
  };

  const ensureGa4Loaded = () => {
    if (typeof window === "undefined") return;
    if (!hasAnalyticsConsent()) return;

    // Avoid duplicate loads
    if (window.__ga4Loaded) return;
    window.__ga4Loaded = true;

    // Ensure dataLayer/gtag stub exists early
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
      window.dataLayer.push(arguments);
    };

    window.gtag("js", new Date());
    // Initial config: disable automatic page_view, Gatsby route updates can send it later.
    window.gtag("config", GA4_ID, { send_page_view: false, anonymize_ip: true });

    // Load gtag.js
    const existing = document.querySelector(`script[src^="https://www.googletagmanager.com/gtag/js?id="]`);
    if (!existing) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
      s.referrerPolicy = "strict-origin-when-cross-origin";
      document.head.appendChild(s);
    }
  };

  // Listen to Cookiebot events: when user submits/changes consent, load GA if allowed.
  // Cookiebot publishes a window event 'CookiebotOnAccept' and 'CookiebotOnDecline' in many setups.
  // We'll hook into accept and also try once after Cookiebot loads.
  const registerCookiebotConsentHooks = () => {
    if (window.__cookiebotHooksRegistered) return;
    window.__cookiebotHooksRegistered = true;

    window.addEventListener("CookiebotOnAccept", () => ensureGa4Loaded());
    window.addEventListener("CookiebotOnConsentReady", () => ensureGa4Loaded());
  };

  // Expose a helper to open consent settings later (Footer link/button)
  // This is safe even before Cookiebot is loaded.
  window.__openCookieSettings = () => {
    try {
      if (window.Cookiebot?.renew) return window.Cookiebot.renew();
      if (window.CookieConsent?.renew) return window.CookieConsent.renew();
      if (window.Cookiebot?.show) return window.Cookiebot.show();
    } catch (e) {
      // noop
    }

    try {
      window.__loadCookiebot?.("open-settings");
      setTimeout(() => {
        try {
          if (window.Cookiebot?.renew) window.Cookiebot.renew();
          else if (window.CookieConsent?.renew) window.CookieConsent.renew();
          else if (window.Cookiebot?.show) window.Cookiebot.show();
        } catch (e) {
          // noop
        }
      }, 400);
    } catch (e) {
      // noop
    }
  };

  const alreadyInjected = () => !!document.getElementById("Cookiebot");

  const showCookieBannerIfNeeded = () => {
    try {
      // Only show if Cookiebot is available and user hasn't provided consent yet.
      // Cookiebot typically exposes consent flags after it is ready.
      if (!window.Cookiebot) return;

      // If consent object is not ready yet, try shortly after.
      if (!window.Cookiebot.consent) {
        setTimeout(showCookieBannerIfNeeded, 300);
        return;
      }

      // If the user already has any settled consent state, don't force-open.
      // We interpret "consent.necessary" as being present when Cookiebot is ready.
      const c = window.Cookiebot.consent;
      const hasAnyChoice =
        typeof c.marketing === "boolean" ||
        typeof c.statistics === "boolean" ||
        typeof c.preferences === "boolean";

      if (hasAnyChoice) return;

      // Open banner/settings once.
      if (window.__cookieBannerShownOnce) return;
      window.__cookieBannerShownOnce = true;

      if (typeof window.Cookiebot.renew === "function") {
        window.Cookiebot.renew();
      } else if (typeof window.Cookiebot.show === "function") {
        window.Cookiebot.show();
      }
    } catch (e) {
      // noop
    }
  };

  const injectCookiebot = () => {
    if (alreadyInjected()) return;

    // Register consent hooks before script loads, so events won't be missed.
    registerCookiebotConsentHooks();

    const s = document.createElement("script");
    s.id = "Cookiebot";
    s.src = "https://consent.cookiebot.com/uc.js";
    s.setAttribute("data-cbid", "bc0e9b79-341b-4787-868f-51890e9da79b");
    s.setAttribute("data-blockingmode", "manual");
    s.type = "text/javascript";
    s.async = true;
    s.defer = true;

    // If Cookiebot is already settled quickly, attempt GA load after it becomes available.
    s.addEventListener("load", () => {
      // Consent might already exist from prior visit
      ensureGa4Loaded();

      // Cookiebot initialisiert Consent/UI manchmal erst nach dem Script-load asynchron.
      // Daher mehrfach anstoßen, um sicher das Banner zu öffnen.
      showCookieBannerIfNeeded();
      setTimeout(showCookieBannerIfNeeded, 300);
      setTimeout(showCookieBannerIfNeeded, 1200);
    });

    document.body.appendChild(s);
  };

  // Provide a global loader so UI can request it (e.g. Footer link)
  window.__loadCookiebot = (reason = "") => {
    if (alreadyInjected()) return;

    const doInject = () => injectCookiebot();
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => doInject(), { timeout: 3000 });
    } else {
      setTimeout(doInject, 500);
    }
  };

  // Load Cookiebot only after real user interaction to prevent it becoming LCP.
  let interactionTriggered = false;
  const loadOnFirstInteraction = () => {
    if (interactionTriggered) return;
    interactionTriggered = true;

    window.__loadCookiebot?.("first-interaction");

    // In case Cookiebot is already present (edge-case), try showing right away.
    // Otherwise this will run again after the script's load handler.
    setTimeout(showCookieBannerIfNeeded, 200);
    setTimeout(showCookieBannerIfNeeded, 800);
  };

  const opts = { once: true, passive: true };

  // Pointer/keyboard are usually reliable
  window.addEventListener("pointerdown", loadOnFirstInteraction, opts);
  window.addEventListener("keydown", loadOnFirstInteraction, opts);

  // Scroll: sehr wichtig -> sofort beim ersten Scroll laden (nicht erst über Debounce)
  window.addEventListener("scroll", loadOnFirstInteraction, opts);

  // Zusätzliche Signale
  window.addEventListener("wheel", loadOnFirstInteraction, opts);
  window.addEventListener("touchstart", loadOnFirstInteraction, opts);

  // Optional safety net: load after some time if there was no interaction.
  setTimeout(() => window.__loadCookiebot?.("timeout"), 15000);
};

// Emotion Cache für Client (muss mit SSR übereinstimmen)
const key = "mui";
const cache = createCache({ key });

// Wrap Root Element mit ThemeProvider und CacheProvider
export const wrapRootElement = ({ element }) => (
  <CacheProvider value={cache}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {element}
    </ThemeProvider>
  </CacheProvider>
);

// Wrap Page Element mit dem ScrollToTopButton
export const wrapPageElement = ({ element }) => (
  <>
    {element}
    <ScrollToTopButton />
  </>
);
